import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SchoolCoursesApiService } from '@digilize/language-school/data';
import { AuthApiService, ResearchSurveyApiService } from '@digilize/shared/data/services/src';
import { SnackbarType, Token } from '@digilize/shared/definitions/src';
import { AuthService } from '@digilize/shared/feature/modules/auth/src';
import { SnackbarData, SnackbarService } from '@digilize/shared/feature/modules/snackbar/src';
import { environment } from '@lsc/src/environments/environment';
import { catchError, retry, Subscription, switchMap, throwError, timer } from 'rxjs';

@Component({
  selector: 'lsc-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  form = new FormGroup({
    email: new FormControl('', { updateOn: 'blur', validators: [Validators.email, Validators.required] }),
  });
  respState: 'none' | 'error' | 'success' | 'expiredCode' = 'none';
  loading = false;
  redirecting = false;
  meSub: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authApiService: AuthApiService,
    private researchSurveyApiService: ResearchSurveyApiService,
    private authService: AuthService,
    private snackbarService: SnackbarService,
    private schoolCoursesApiService: SchoolCoursesApiService
  ) {}

  ngOnInit(): void {
    this.handleLoginCodeParam();
    this.handleTokenParam();

    if (this.authService.isLoggedIn()) {
      this.loading = true;
      this.navigateToMap();
    }
  }

  private handleLoginCodeParam() {
    const loginCode = this.route.snapshot.queryParamMap.get('login_code');
    if (loginCode) {
      this.authService.logout(null);
      this.loading = true;
      this.meSub = this.authApiService
        .authWithCode(loginCode)
        .pipe(
          catchError((err) => {
            this.respState = 'expiredCode';
            this.loading = false;
            return throwError(err);
          }),
          switchMap((resp: Token) => {
            this.authService.setSession(resp);
            return this.authApiService.me;
          })
        )
        .subscribe({
          next: (resp) => {
            this.navigateToMap();
          },
        });
    }
  }
  navigateToMap() {
    this.schoolCoursesApiService.getActiveCourses().subscribe((courses) => {
      if (courses.courses.length === 0) {
        this.router.navigate(['course', 'noCourse', 'noCourse']);
        return;
      }
      const actualCourseId = localStorage.getItem('actualCourseId') || courses.courses[0].id;
      this.router.navigate(['course', actualCourseId]);
    });
  }
  private handleTokenParam() {
    const token = this.route.snapshot.queryParamMap.get('token');

    if (token) {
      this.loading = true;
      this.redirecting = true;
      this.authService.logout(null);
      this.researchSurveyApiService.start(token).subscribe({
        next: (resp) => {
          this.authService.setSession(resp.system_auth);
          this.loading = false;
          if (environment.surveyStartUrl) {
            window.location.href = `${environment.surveyStartUrl}/${resp.dms_session_id}`;
          }
        },
        error: (err) => {
          this.redirecting = false;
        },
      });
    }
  }

  handleLoginClick() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading = true;
    this.authApiService
      .authRequest(this.form.controls.email.value)
      .pipe(
        retry({
          count: 3,
          delay: (error, count) => {
            return timer(Math.min(10000, 2 ^ (count * 1000)));
          },
        })
      )
      .subscribe({
        next: (resp) => {
          this.loading = false;
          this.respState = 'success';
          const data: SnackbarData = {
            translate: false,
            msg: `The email was sent to ${this.form.controls.email.value}`,
            withoutIcon: true,
          };
          this.snackbarService.openSnackbar(SnackbarType.Information, { data });
        },
        error: () => {
          this.loading = false;
          this.respState = 'error';
        },
      });
  }

  ngOnDestroy(): void {
    if (this.meSub) this.meSub.unsubscribe();
  }
}
